// imports
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// local imports
import { useUrlParameters } from '../../hooks/useUrlParameters';
import { mapActions, RootState } from '../../store';
import { MAP_MIN_ZOOM, MAP_MAX_ZOOM } from '../../constants/mapZoom';

/**
 * MapViewInitializer
 *
 * Component that initializes the map view state (zoom, latitude, longitude) from URL parameters on load and updates parameters as the view state changes.
 */
export const MapViewInitializer = () => {
  const dispatch = useDispatch();
  const { getParameter, setParameter } = useUrlParameters();
  const currentViewState = useSelector(
    (state: RootState) => state.map.viewState
  );

  // Threshold for triggering lat/lng updates
  // This is so that it doesn't update when user is rapidly panning
  const LAT_LNG_THRESHOLD = 0.001;

  useEffect(() => {
    // Get all URL parameters first
    const zoomFromUrl = getParameter('zoom');
    const latFromUrl = getParameter('lat');
    const lngFromUrl = getParameter('lon');

    const updates: Partial<typeof currentViewState> = {};

    // Handle zoom parameter
    let parsedZoom = null;
    if (zoomFromUrl) {
      parsedZoom = parseFloat(parseFloat(zoomFromUrl).toFixed(1));
    }

    if (
      parsedZoom !== null &&
      !isNaN(parsedZoom) &&
      parsedZoom >= MAP_MIN_ZOOM &&
      parsedZoom <= MAP_MAX_ZOOM &&
      parsedZoom !== parseFloat(currentViewState.zoom.toFixed(1))
    ) {
      updates.zoom = parsedZoom;
      setParameter('zoom', parsedZoom.toFixed(1));
    } else if (
      parsedZoom !== null &&
      !isNaN(parsedZoom) &&
      (parsedZoom < MAP_MIN_ZOOM || parsedZoom > MAP_MAX_ZOOM)
    ) {
      const clamped = Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, parsedZoom));
      updates.zoom = clamped;
      setParameter('zoom', clamped.toFixed(1));
    } else if (
      !zoomFromUrl ||
      parsedZoom === null ||
      isNaN(parsedZoom)
    ) {
      setParameter('zoom', currentViewState.zoom.toFixed(1));
    }

    // Handle latitude parameter
    let parsedLat = null;
    if (latFromUrl) {
      parsedLat = parseFloat(latFromUrl);
    }

    if (
      parsedLat !== null &&
      !isNaN(parsedLat) &&
      parsedLat >= -90 &&
      parsedLat <= 90 &&
      Math.abs(parsedLat - currentViewState.latitude) > LAT_LNG_THRESHOLD
    ) {
      updates.latitude = parsedLat;
    } else if (
      !latFromUrl ||
      parsedLat === null ||
      isNaN(parsedLat) ||
      parsedLat < -90 ||
      parsedLat > 90
    ) {
      setParameter('lat', currentViewState.latitude.toFixed(6));
    }

    // Handle longitude parameter
    let parsedLng = null;
    if (lngFromUrl) {
      parsedLng = parseFloat(lngFromUrl);
    }

    if (
      parsedLng !== null &&
      !isNaN(parsedLng) &&
      parsedLng >= -180 &&
      parsedLng <= 180 &&
      Math.abs(parsedLng - currentViewState.longitude) > LAT_LNG_THRESHOLD
    ) {
      updates.longitude = parsedLng;
    } else if (
      !lngFromUrl ||
      parsedLng === null ||
      isNaN(parsedLng) ||
      parsedLng < -180 ||
      parsedLng > 180
    ) {
      setParameter('lon', currentViewState.longitude.toFixed(6));
    }

    // Dispatch all updates at once if needed
    if (Object.keys(updates).length > 0) {
      dispatch(mapActions.setViewState(updates));
    }

    // Run only on mount - do not update map according to URL parameters after initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This component doesn't render anything
  return null;
};

export default MapViewInitializer;
